import type { FlowScreenConfig } from "@/shared/constants/flowTypes";
import type { FamilyTriagePersonRow } from "@/shared/types/probateTypes";

import { FamilyTriageIntroScreen } from "../components/family-triage/FamilyTriageIntroScreen";
import { MarriagesScreen } from "../components/family-triage/MarriagesScreen";
import { ChildrenScreen } from "../components/family-triage/ChildrenScreen";
import { ExtendedFamilyScreen } from "../components/family-triage/ExtendedFamilyScreen";
import { ReviewScreen } from "../components/family-triage/ReviewScreen";
import { LoadingScreen } from "../components/family-triage/LoadingScreen";
import { RevealScreen } from "../components/family-triage/RevealScreen";

function validatePersonRows(
  rows: FamilyTriagePersonRow[],
  prefix: string,
  errors: Record<string, string>,
) {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const parts = row.name.trim().split(" ");
    if (!parts[0]) errors[`${prefix}_${i}_first_name`] = "First name is required";
    if (parts.length < 2 || !parts.slice(1).join(" ").trim())
      errors[`${prefix}_${i}_last_name`] = "Last name is required";
    if (!row.status) errors[`${prefix}_${i}_status`] = "Status is required";
    if (row.status === "passed_away" && row.hasChildren === "yes") {
      validatePersonRows(row.children, `${prefix}_${i}_gc`, errors);
    }
  }
}

export function createFamilyTriageScreens(): FlowScreenConfig[] {
  return [
    {
      id: "family_triage_intro",
      layout: "full",
      customComponent: FamilyTriageIntroScreen,
      getButtonText: () => "Get started",
    },
    {
      id: "family_triage_marriages",
      layout: "full",
      customComponent: MarriagesScreen,
      validate: (answers) => {
        const errors: Record<string, string> = {};
        const married = answers["family_triage_married_at_death"];

        if (!married) {
          errors.married_at_death = "Select an option to continue";
          return errors;
        }

        if (married === "yes") {
          if (!answers["family_triage_spouse_first_name"]) errors.spouse_first_name = "First name is required";
          if (!answers["family_triage_spouse_last_name"]) errors.spouse_last_name = "Last name is required";
          if (!answers["family_triage_spouse_status"]) errors.spouse_status = "Status is required";
          const spouseYear = answers["family_triage_spouse_death_year"] as string;
          if (spouseYear && (spouseYear.length < 4 || Number(spouseYear) > new Date().getFullYear())) {
            errors.spouse_death_year = `Enter a valid year (up to ${new Date().getFullYear()})`;
          }
          if (Object.keys(errors).length > 0) return errors;
        }

        const earlier = answers["family_triage_earlier_marriages"];
        if (married && !earlier) {
          errors.earlier_marriages = "Select an option to continue";
          return errors;
        }

        if (earlier === "yes") {
          try {
            const list = answers["family_triage_earlier_marriages_list"];
            if (typeof list === "string") {
              const currentYear = new Date().getFullYear();
              const marriages = JSON.parse(list) as Array<{
                firstName: string;
                lastName: string;
                endYear: string;
                endReason: string;
              }>;
              for (let i = 0; i < marriages.length; i++) {
                const m = marriages[i];
                if (!m.firstName) errors[`marriage_${i}_first_name`] = "First name is required";
                if (!m.lastName) errors[`marriage_${i}_last_name`] = "Last name is required";
                if (!m.endReason) errors[`marriage_${i}_end_reason`] = "Select how the marriage ended";
                if (m.endYear && (m.endYear.length < 4 || Number(m.endYear) > currentYear)) {
                  errors[`marriage_${i}_end_year`] = `Enter a valid year (up to ${currentYear})`;
                }
              }
            }
          } catch {
            /* skip */
          }
        }

        return errors;
      },
    },
    {
      id: "family_triage_children",
      layout: "full",
      customComponent: ChildrenScreen,
      validate: (answers) => {
        const errors: Record<string, string> = {};
        const hasChildren = answers["family_triage_has_children"];

        if (!hasChildren) {
          errors.has_children = "Select an option to continue";
          return errors;
        }

        if (hasChildren === "yes") {
          try {
            const list = answers["family_triage_children_list"];
            if (typeof list === "string") {
              const children = JSON.parse(list) as FamilyTriagePersonRow[];
              validatePersonRows(children, "child", errors);
            }
          } catch {
            /* skip */
          }
        }

        return errors;
      },
    },
    {
      id: "family_triage_extended_family",
      layout: "full",
      customComponent: ExtendedFamilyScreen,
      shouldDisplay: ({ answers }) => {
        const married = answers["family_triage_married_at_death"];
        const spouseStatus = answers["family_triage_spouse_status"];
        const hasChildren = answers["family_triage_has_children"];
        const hasLivingSpouse = married === "yes" && spouseStatus === "living";

        // Check for living descendants
        let hasLivingDescendant = false;
        if (hasChildren === "yes") {
          try {
            const list = answers["family_triage_children_list"];
            if (typeof list === "string") {
              const children = JSON.parse(list) as FamilyTriagePersonRow[];
              const checkDescendants = (rows: FamilyTriagePersonRow[]): boolean => {
                for (const row of rows) {
                  if (row.status === "living" || row.status === "not_sure") return true;
                  if (row.status === "passed_away" && row.hasChildren === "yes" && checkDescendants(row.children)) return true;
                }
                return false;
              };
              hasLivingDescendant = checkDescendants(children);
            }
          } catch { /* skip */ }
        }

        // Show when: (living spouse + no children/descendants) OR (no living spouse + no living descendants)
        if (hasLivingSpouse && !hasLivingDescendant) return true;
        if (!hasLivingSpouse && !hasLivingDescendant) return true;
        return false;
      },
      validate: (answers) => {
        const errors: Record<string, string> = {};
        const married = answers["family_triage_married_at_death"];
        const spouseStatus = answers["family_triage_spouse_status"];
        const hasLivingSpouse = married === "yes" && spouseStatus === "living";

        if (hasLivingSpouse) {
          // Conditional parents mode: validate parent rows
          try {
            const list = answers["family_triage_parents_list"];
            if (typeof list === "string") {
              const parents = JSON.parse(list) as FamilyTriagePersonRow[];
              validatePersonRows(parents, "parent", errors);
            }
          } catch { /* skip */ }
          return errors;
        }

        // Extended family mode
        const hasParents = answers["family_triage_has_living_parents"];
        if (!hasParents) {
          errors.has_living_parents = "Select an option to continue";
          return errors;
        }

        let needsSiblings = hasParents === "no_living_parents";

        if (hasParents === "yes") {
          let allParentsDeceased = false;
          try {
            const list = answers["family_triage_parents_list"];
            if (typeof list === "string") {
              const parents = JSON.parse(list) as FamilyTriagePersonRow[];
              validatePersonRows(parents, "parent", errors);
              allParentsDeceased = parents.length > 0 && parents.every((p) => p.status === "passed_away");
            }
          } catch { /* skip */ }
          if (Object.keys(errors).length > 0) return errors;
          if (allParentsDeceased) needsSiblings = true;
        }

        if (needsSiblings) {
          const hasSiblings = answers["family_triage_has_siblings"];
          if (!hasSiblings) {
            errors.has_siblings = "Select an option to continue";
            return errors;
          }
          if (hasSiblings === "yes") {
            try {
              const list = answers["family_triage_siblings_list"];
              if (typeof list === "string") {
                const siblings = JSON.parse(list) as FamilyTriagePersonRow[];
                validatePersonRows(siblings, "sibling", errors);
              }
            } catch { /* skip */ }
          }
        }

        return errors;
      },
    },
    {
      id: "family_triage_review",
      layout: "full",
      customComponent: ReviewScreen,
    },
    {
      id: "family_triage_loading",
      layout: "full",
      hideButton: true,
      skipOnBack: true,
      customComponent: LoadingScreen,
    },
    {
      id: "family_triage_reveal",
      layout: "full",
      customComponent: RevealScreen,
      getButtonText: () => "Save and continue",
    },
  ];
}
